import { Form, Formik } from 'formik'
import { useRouter } from 'next/router'
import { useRef, useState, type FC } from 'react'
import { Button } from '../Button/Button'
import { RichText } from '../RichText/RichText'
import FormComponents from './FormComponents'
import {
  FormKeys,
  type SignUpFormProps,
  type Values
} from './SignUpForm.interface'
import styles from './SignUpForm.module.scss'
import { validationSchema } from './SignUpForm.validation'
import { BusinessSelector } from './FormComponents/FormSelect/BusinessSelector'

const initalValues: Values = {
  businessName: '',
  websiteUrl: '',
  telephoneNumber: '',
  address: '',
  shortDescription: '',
  longDescription: '',
  isIndependant: '',
  businessType: '',
  categories: [],
  primaryImage: '',
  secondaryImage: '',
  thirdImage: '',
  contactName: '',
  contactEmail: '',
  captchaToken: '',
  pluralisedCategoryName: ''
}

const uploadImage = async (
  file: File,
  name: string,
  folderId: string,
  signature: string,
  nonce: string
): Promise<string> => {
  if (!file) return ''
  const formData = new FormData()
  formData.append('file', file)
  formData.append('businessName', name)
  formData.append('folderId', folderId)
  formData.append('signature', signature)
  formData.append('nonce', nonce)
  const response = await fetch('/api/sign-up/upload-image', {
    method: 'POST',
    body: formData
  })

  try {
    if (!response.ok) {
      throw new Error('Failed to upload image')
    }
    const data = await response.json()
    const imageFilePath = data.filePath

    return imageFilePath
  } catch (e) {
    throw new Error(e as string)
  }
}

export const SignUpForm: FC<SignUpFormProps> = ({
  businessTitle,
  businessDescription,
  categoriesHeading,
  categoriesDescription,
  categories,
  photosDescription,
  photosHeading,
  contactDetailsHeading,
  contactDetailsDescription,
  submitButton
}) => {
  const router = useRouter()

  const primaryImageRef = useRef<HTMLInputElement>(null)
  const secondaryImageRef = useRef<HTMLInputElement>(null)
  const thirdImageRef = useRef<HTMLInputElement>(null)

  const [error, setError] = useState<string | boolean>('')

  const handleSubmit = async (values: Values): Promise<void> => {
    if (values[FormKeys.IS_INDEPENDANT] === 'no') {
      setError(
        'We only promote independent businesses. As your business is not independent, we cannot accept your application.'
      )
      return
    }

    const businessName = values[FormKeys.BUSINESS_NAME]

    try {
      const submitResponse = await fetch('/api/sign-up/submit-listing', {
        method: 'POST',
        headers: {
          'content-type': 'application/json'
        },
        body: JSON.stringify({
          ...values,
          captchaToken: values[FormKeys.CAPTCHA]
        })
      })

      const submittedResponse = (await submitResponse.json()) as {
        ok: boolean;
        storyId: string;
        error: string;
        signature: string;
        nonce: string;
      }

      if (!submittedResponse.ok) {
        if (submittedResponse.error.includes('Unprocessable Content')) {
          throw new Error(`${values[FormKeys.BUSINESS_NAME]} is already taken`)
        }
        throw new Error(submittedResponse.error)
      }

      const { signature, nonce } = submittedResponse

      const folderResponse = await fetch('/api/sign-up/create-folder', {
        method: 'POST',
        headers: {
          'content-type': 'application/json'
        },
        body: JSON.stringify({
          businessName,
          signature,
          nonce
        })
      })

      const folderId = (await folderResponse.json()) as string

      try {
        const primaryImage =
          primaryImageRef?.current?.files &&
          (await uploadImage(
            primaryImageRef?.current.files[0],
            businessName,
            folderId,
            signature,
            nonce
          ))
        const secondaryImage =
          secondaryImageRef?.current?.files &&
          (await uploadImage(
            secondaryImageRef.current.files[0],
            businessName,
            folderId,
            signature,
            nonce
          ))

        const thirdImage =
          thirdImageRef?.current?.files &&
          (await uploadImage(
            thirdImageRef.current.files[0],
            businessName,
            folderId,
            signature,
            nonce
          ))

        await fetch('/api/sign-up/update-listing', {
          method: 'POST',
          headers: {
            'content-type': 'application/json'
          },
          body: JSON.stringify({
            ...values,
            primaryImage,
            secondaryImage,
            thirdImage,
            id: submittedResponse.storyId,
            signature,
            nonce
          })
        })
      } catch (ex) {
        await fetch('/api/sign-up/delete-story', {
          method: 'POST',
          headers: {
            'content-type': 'application/json'
          },
          body: JSON.stringify({
            id: submittedResponse.storyId,
            signature,
            nonce
          })
        })
        throw new Error('Failed to upload images')
      }

      await router.push('/confirm-signup')
    } catch (err) {
      // If Failed try delete story so can retry upload

      const e = err as Error
      setError(
        e?.message ??
          'Something went wrong. Please try again or contact Ridgeway'
      )
    }
  }

  return (
    <div className={styles.container}>
      <section>
        <div className={styles['about-container']}>
          <h2 className={styles['about-title']}>{businessTitle}</h2>
          <RichText
            className={styles['about-text']}
            richtext={businessDescription.richtext}
          />
        </div>

        <Formik
          initialValues={initalValues}
          onSubmit={async (values, { setSubmitting }) => {
            setSubmitting(true)
            await handleSubmit(values)
            setSubmitting(false)
          }}
          validationSchema={validationSchema}
        >
          {({ values, isSubmitting, errors, submitCount }) => (
            <Form className={styles.form}>
              <div className={styles['business-form']}>
                <FormComponents.Input
                  name={FormKeys.BUSINESS_NAME}
                  label="Business name"
                  autoComplete="organization"
                />
                <FormComponents.Input
                  name={FormKeys.WEBSITE_URL}
                  label="Website URL"
                  autoComplete="url"
                />
                <FormComponents.Input
                  name={FormKeys.TELEPHONE_NUMBER}
                  label="Telephone number"
                  optionalText="optional"
                  type="tel"
                  autoComplete="tel"
                />
                <FormComponents.Input
                  name={FormKeys.ADDRESS}
                  label="Address"
                  autoComplete="street-address"
                />

                <FormComponents.InputWithStringLengthCheck
                  name={FormKeys.SHORT_DESCRIPTION}
                  maxLength={40}
                  label="Short description"
                />
                <FormComponents.InputWithStringLengthCheck
                  name={FormKeys.LONG_DESCRIPTION}
                  maxLength={120}
                  label="Long description"
                />
                <FormComponents.IndependantCheck
                  label="Are you independent?"
                  placeholder="Please select"
                  name={FormKeys.IS_INDEPENDANT}
                  setMainError={setError}
                />
                <BusinessSelector
                  label="What type of business are you?"
                  name={FormKeys.BUSINESS_TYPE}
                  categories={categories}
                  placeholder="Please select"
                />
              </div>

              {values[FormKeys.BUSINESS_TYPE] &&
                values[FormKeys.IS_INDEPENDANT] === 'yes' && (
                  <div>
                    <div className={styles['category-container']}>
                      <h3 className={styles['category-title']}>
                        {categoriesHeading}
                      </h3>
                      <RichText
                        className={styles['category-text']}
                        richtext={categoriesDescription.richtext}
                      />
                    </div>

                    <FormComponents.Categories
                      categories={categories}
                      name={FormKeys.CATEGORIES}
                    />

                    <div className={styles['photos-container']}>
                      <h3 className={styles['category-title']}>
                        {photosHeading}
                      </h3>
                      <RichText
                        className={styles['category-text']}
                        richtext={photosDescription.richtext}
                      />

                      <div className={styles['category-image-uploads']}>
                        <FormComponents.File
                          label="Primary image"
                          name={FormKeys.PRIMARY_IMAGE}
                          imageRef={primaryImageRef}
                        />
                        <FormComponents.File
                          label="Second image"
                          name={FormKeys.SECONDARY_IMAGE}
                          imageRef={secondaryImageRef}
                          optionalText="optional"
                        />
                        <FormComponents.File
                          label="Third image"
                          name={FormKeys.THIRD_IMAGE}
                          imageRef={thirdImageRef}
                          optionalText="optional"
                        />
                      </div>
                    </div>

                    <div className={styles['contact-container']}>
                      <h3 className={styles['contact-title']}>
                        {contactDetailsHeading}
                      </h3>
                      <RichText
                        className={styles['contact-text']}
                        richtext={contactDetailsDescription.richtext}
                      />

                      <div className={styles['contact-form']}>
                        <FormComponents.Input
                          name={FormKeys.CONTACT_NAME}
                          label="Your name"
                          autoComplete="name"
                        />
                        <FormComponents.Input
                          name={FormKeys.CONTACT_EMAIL}
                          label="Email Address"
                          autoComplete="email"
                        />
                      </div>
                    </div>

                    <FormComponents.Captcha name={FormKeys.CAPTCHA} />
                  </div>
              )}
              <p className={styles['error-message']}>
                {submitCount > 0 && Object.values(errors).length > 0
                  ? 'Please correct the highlighted fields above'
                  : error}
              </p>

              <Button
                disabled={
                  isSubmitting || values[FormKeys.IS_INDEPENDANT] === 'no'
                }
                className={styles['submit-button']}
                type="submit"
                size="large"
              >
                {!isSubmitting ? submitButton.children : 'Submitting'}
              </Button>
            </Form>
          )}
        </Formik>
      </section>
    </div>
  )
}
